import { TrainingsContentBlockInput } from 'generated/resources/graphql'

import { QuizBlock } from 'modules/trainings/lib/content/QuizBlock'
import { BlockMapper } from 'modules/trainings/lib/content/mappers/graphql/BlockMapperRegistry'
import { MapperTransformationError } from 'modules/trainings/lib/content/mappers/graphql/errors'
import { GQLContentBlock } from 'modules/trainings/lib/content/mappers/graphql/types'

function isQuizContentable(contentable: GQLContentBlock['contentable']): contentable is NonNullable<
  GQLContentBlock['contentable']
> & {
  __typename: 'TrainingsContentBlockQuiz'
} {
  return contentable?.__typename === 'TrainingsContentBlockQuiz'
}

export const quizBlockMapper: BlockMapper<QuizBlock> = {
  graphqlTypename: 'TrainingsContentBlockQuiz',
  blockConstructor: QuizBlock,

  fromGraphQL(gqlBlock: GQLContentBlock): QuizBlock {
    const contentable = gqlBlock.contentable

    if (!isQuizContentable(contentable)) {
      throw new MapperTransformationError(
        'TrainingsContentBlockQuiz',
        `Expected TrainingsContentBlockQuiz, got ${contentable?.__typename || 'null'}`,
        gqlBlock
      )
    }

    return new QuizBlock({
      uuid: gqlBlock.uuid,
      title: gqlBlock.title,
      form: contentable.form
        ? {
            id: contentable.form.id,
            title: contentable.form.title,
            description: contentable.form.description,
            resourceType: contentable.form.resourceType,
            elements: contentable.form.elements.map((element) => ({
              id: element.id,
              label: element.label,
              description: element.description,
              type: element.type,
              position: element.position,
              mandatory: true,
              hasCorrectAnswer: true,
              options: element.options.map((option) => ({
                id: option.id,
                label: option.label,
                value: option.value,
                isCorrect: option.isCorrect,
              })),
            })),
            configuration: contentable.form.configuration
              ? {
                  allowedElements: contentable.form.configuration.allowedElements,
                  retryable: contentable.form.configuration.retryable,
                  scorable: contentable.form.configuration.scorable,
                }
              : undefined,
          }
        : undefined,
    })
  },

  toGraphQL(block: QuizBlock): TrainingsContentBlockInput {
    return {
      uuid: block.uuid,
      title: block.title,
      type: 'quiz',
      typeQuiz: block.form
        ? {
            form: {
              id: block.form.id,
              title: block.form.title,
              description: block.form.description,
              resourceType: block.form.resourceType,
              resourceId: block.form.resourceId,
              elements: block.form.elements.map((element) => ({
                id: element.id,
                label: element.label,
                description: element.description || '',
                type: element.type,
                position: element.position,
                mandatory: true,
                hasCorrectAnswer: true,
                options: element.options?.map((option) => ({
                  id: option.id,
                  label: option.label,
                  value: option.value,
                  isCorrect: option.isCorrect,
                })),
              })),
            },
          }
        : null,
    }
  },
}
